import { ApolloError, useMutation } from '@apollo/client'
import { tap } from 'ramda'
import { useContext, useMemo, useState } from 'react'

import { QueriedAutoShip } from '../../components/shop/account/auto_ships/types'
import { AddSimpleProductsToCartDocument } from '../../graphql/magento'
import { TokenContext } from '../../layouts/context'
import { publishAddToCartEventForItems } from '../../lib/analytics/cart'
import { useCartContext } from '../../lib/cart'
import { useAddToCartToast } from '../../lib/cart/hooks/useAddToCartToast'
import { OrderType } from '../../lib/cart/item'
import { logAndCaptureException } from '../../utils/errorTools'
import { checkQueryResponse } from '../../utils/graphqlTools'
import useLogAndCaptureError from '../useLogAndCaptureError'

const parseInterval = (frequency: string) => {
  switch (frequency) {
    case 'Every four weeks':
      return 1
    case 'Every two months':
      return 2
    case 'Every three months':
      return 3
    default:
      return 0
  }
}

export const useAutoShipOrder = (autoShip: QueriedAutoShip) => {
  const token = useContext(TokenContext)
  const { cart } = useCartContext()
  const { showSuccessToast } = useAddToCartToast()

  const [ordering, setOrdering] = useState(false)

  const [addSimpleProductsToCart, { error: orderError }] = useMutation(
    AddSimpleProductsToCartDocument,
    {
      context: { token },
    },
  )
  useLogAndCaptureError(orderError)

  const autoShipInterval = useMemo(() => parseInterval(autoShip.frequency), [autoShip.frequency])

  const addToCart = useMemo(() => {
    if (!cart?.id) {
      return null
    }
    return () =>
      new Promise<void>((resolve, reject) => {
        setOrdering(true)

        addSimpleProductsToCart({
          variables: {
            cartId: cart.id,
            cartItems: autoShip.items.map((item) => ({
              data: {
                sku: item ? item.sku : '',
                quantity: item ? item.quantity : 0,
                dfh_order_type: OrderType.AutoShip,
                dfh_autoship_interval: autoShipInterval,
              },
            })),
          },
        })
          .then(checkQueryResponse)
          .then(
            tap(
              publishAddToCartEventForItems({
                items: autoShip.items.map((item) => ({
                  sku: item ? item.sku : '',
                  quantity: item ? item.quantity : 0,
                })),
              }),
            ),
          )
          .then((data) => {
            if (data?.addSimpleProductsToCart) {
              showSuccessToast({
                skus: autoShip.items.map((item) => (item ? item.sku : '')),
              })
            }
          })
          .catch((err: unknown) => {
            if (!(err instanceof ApolloError)) {
              logAndCaptureException(err)
              reject(err)
            }
          })
          .finally(() => {
            setOrdering(false)
            resolve()
          })
      })
  }, [addSimpleProductsToCart, cart, showSuccessToast, autoShip.items, autoShipInterval])

  return { addToCart, ordering, orderError }
}
